import {
  createSystem,
  defaultConfig,
  defineGlobalStyles,
  defineSemanticTokens
} from "@chakra-ui/react";

import { RECAPTCHA_CHALLENGE_IFRAME_SELECTOR } from "@/helpers/recaptcha-overlay";

import { recipes } from "./recipes";
import { slotRecipes } from "./slot-recipes";
import {
  breakpoints,
  mergeThemeTokens,
  tokens,
  type EhqThemeOverrides
} from "./tokens";

/** Flat breakpoint map for `createSystem` — responsive hooks read `theme.breakpoints`, not token defs alone. */
const themeBreakpoints = {
  sm: breakpoints.sm.value,
  md: breakpoints.md.value,
  lg: breakpoints.lg.value,
  xl: breakpoints.xl.value,
  "2xl": breakpoints["2xl"].value
};

export const semanticTokens = defineSemanticTokens({
  colors: {
    border: {
      DEFAULT: { value: "{colors.gray.500}" },
      emphasized: { value: "{colors.gray.600}" }
    },
    brand: {
      solid: { value: "{colors.brand.500}" },
      contrast: { value: "{colors.white}" },
      fg: { value: "{colors.brand.700}" },
      muted: { value: "{colors.brand.100}" },
      subtle: { value: "{colors.brand.50}" },
      emphasized: { value: "{colors.brand.200}" },
      focusRing: { value: "{colors.brand.500}" },
      border: { value: "{colors.brand.500}" }
    },
    facebook: {
      solid: { value: "{colors.facebook.500}" },
      contrast: { value: "{colors.white}" },
      fg: { value: "{colors.facebook.600}" },
      muted: { value: "{colors.facebook.100}" },
      subtle: { value: "{colors.facebook.50}" },
      emphasized: { value: "{colors.facebook.200}" },
      focusRing: { value: "{colors.facebook.500}" },
      border: { value: "{colors.facebook.500}" }
    },
    linkedin: {
      solid: { value: "{colors.linkedin.500}" },
      contrast: { value: "{colors.white}" },
      fg: { value: "{colors.linkedin.600}" },
      muted: { value: "{colors.linkedin.100}" },
      subtle: { value: "{colors.linkedin.50}" },
      emphasized: { value: "{colors.linkedin.200}" },
      focusRing: { value: "{colors.linkedin.500}" },
      border: { value: "{colors.linkedin.500}" }
    },
    green: {
      solid: { value: "{colors.feedback.success}" }
    },
    red: {
      solid: { value: "{colors.feedback.error}" }
    },
    orange: {
      solid: { value: "{colors.feedback.warning}" }
    },
    blue: {
      solid: { value: "{colors.blue.500}" }
    }
  }
});

export function buildGlobalCss() {
  return defineGlobalStyles({
    body: {
      // Do NOT set `font-size` here: v2 never did, so un-sized text must inherit
      // the browser default, not the API `baseFontSize` em value.
      color: "gray.700",
      fontFamily: "body",
      _dark: { color: "whiteAlpha.900" }
    },
    a: {
      color: "brand.500",
      textDecoration: "underline",
      _hover: {
        textDecoration: "underline"
      },
      _focusVisible: {
        outline: "none",
        borderRadius: "md",
        boxShadow: "outline"
      }
    },
    ".ehq-participant-footer a": {
      color: "inherit",
      textDecoration: "none",
      _hover: {
        color: "inherit",
        textDecoration: "none"
      }
    },
    ".ehq-participant-footer__static-pages a:hover": {
      textDecoration: "underline"
    },
    // v2 forced white link text on solid feedback surfaces via
    // `.chakra-alert a` — v2 toasts rendered as Alerts, so that one rule
    // covered both. v3 slot recipes emit `${key}__${slot}` classes (see
    // @chakra-ui/react/.../sva.cjs), so Alert is `chakra-alert__root` and
    // Toast is `chakra-toast__root`; the bare `chakra-alert` class never
    // appears. Without these retargeted selectors, links inside solid
    // Alerts/toasts fall back to the global `a { color: brand.500 }` rule
    // and render brand-colored on the brand-tinted background.
    ".chakra-alert__root a, .chakra-alert__root button": {
      color: "whiteAlpha.900"
    },
    ".chakra-toast__root a, .chakra-toast__root button": {
      color: "whiteAlpha.900"
    },
    // Modal dialogs set `pointer-events: none` on <body> to block the page
    // behind them. reCAPTCHA appends its challenge overlay to <body> too, so it
    // inherits that and its tiles become unclickable — opt it back in. Browsers
    // without `:has()` support fall back to useRecaptchaOverlayPointerEvents.
    [`body > div:has(${RECAPTCHA_CHALLENGE_IFRAME_SELECTOR})`]: {
      pointerEvents: "auto !important"
    },
    ".recharts-default-legend": {
      marginBottom: "16px !important"
    },
    ".recharts-default-legend svg": {
      borderRadius: "base"
    },
    ".recharts-default-legend li": {
      verticalAlign: "middle"
    },
    ".recharts-legend-item-text": {
      color: "gray.700 !important",
      _dark: { color: "whiteAlpha.500 !important" }
    },
    "*::placeholder": {
      color: "gray.600",
      _dark: { color: "whiteAlpha.400" }
    },
    "*, *::before, *::after": {
      borderColor: "gray.500",
      _dark: { borderColor: "whiteAlpha.300" }
    }
  });
}

export const globalCss = buildGlobalCss();

export function buildSystem(overrides: EhqThemeOverrides = {}) {
  return createSystem(defaultConfig, {
    theme: {
      breakpoints: themeBreakpoints,
      tokens: mergeThemeTokens(overrides),
      semanticTokens,
      recipes,
      slotRecipes
    },
    globalCss
  });
}

export const defaultSystem = buildSystem();

export { tokens, recipes, slotRecipes, globalCss as systemGlobalCss };
